import React, { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { collabApi } from "../../services/collabApi";
import { dateFormat } from "../../utils/dateUtils";

/**
 * CoOwnershipPanel — who co-owns this campaign, and the one write staff need.
 *
 * Before this, the admin console had NO surface for campaign_participants or
 * campaign_invitations at all: staff could not see who co-owned a campaign, and
 * the only way to unwind a bad co-owner was a hand-rolled API call.
 *
 * WHY THE API IS THE AUTHORITY, and this panel makes no access decision.
 *
 * The obvious move was to gate on hasRole('AD'), mirroring the API's bypass.
 * Rendering it on dev proved that wrong, and the failure is worth recording
 * because it is this programme's recurring shape — two sides answering the same
 * question from different sources:
 *
 *   API   callerHoldsAdminBypass -> resolveActiveRole(userId) === 'AD' when
 *         feature.canonical_admin_login is ARMED, else users.admin_status === 1
 *   UI    useRole().hasRole('AD')  -> role_name from the LOGIN RESPONSE
 *
 * While the flag is DARK those disagree. Measured on dev 2026-07-28:
 * admin@rootsfundme.com (id 14) has admin_status = 1 AND an ACTIVE 'AD'
 * assignment, so the API grants it — but the flag is dark there, so the login
 * response labels the account "Dealing Representative (retired)" and
 * hasRole('AD') is FALSE. The panel would have been invisible to the very
 * account used to test it, on the environment it is tested on, while the API
 * served it perfectly well. Prod is the opposite: the flag is armed there. A
 * gate that inverts between environments is worse than no gate.
 *
 * So: anyone reaching this page already passed ProtectedRoutes
 * requiredPath="Campaign - <Status>:View". The API then decides whether they
 * may read the roster, and a 403 renders as one quiet line rather than a red
 * error. Nothing can leak, because the data never arrives without the API's
 * consent — the only gate that cannot drift from itself.
 *
 * WHY no Revoke or Invite button. Both mint and revoke sit behind the
 * `collab_enabled` feature gate. Its migration seeds the flag false and
 * PRODUCTION HAS NEVER RUN THAT MIGRATION, so featureGate fails closed with a
 * 503 there. Shipping either control would put a button on screen that cannot
 * work on the environment that matters. Pending invitations are shown read-only
 * so staff can at least SEE them; the absence of the action is deliberate and
 * recorded rather than forgotten.
 *
 * WHY no Remove on the creator. A25 (owner decision, 2026-07-28) made
 * CREATOR_PROTECTED unconditional — admins included — because
 * campaign_details.user_id is the immutable fee / payout / agreement / PII
 * anchor and nothing in any repo can move it. Removing the creator would split
 * their rights from those anchors permanently. The panel states that rather
 * than offering a button that 409s.
 */
const CoOwnershipPanel = ({ campaignId, campaignOwnerId }) => {
  const [participants, setParticipants] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [busyUserId, setBusyUserId] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    setLoadError(null);
    setForbidden(false);
    try {
      const [p, i] = await Promise.all([
        collabApi.listParticipants(campaignId),
        collabApi.listInvitations(campaignId),
      ]);
      setParticipants(p?.data || []);
      setInvitations(i?.data || []);
    } catch (err) {
      // Fail LOUD on a real failure. An empty roster and a failed fetch look
      // identical, and this panel exists precisely so staff can trust what they
      // are seeing.
      //
      // A 403 is NOT a failure though — it is the API saying this staff member
      // may not see the roster, which is the access decision the panel
      // deliberately delegates. Render it as one quiet line, not a red error.
      const status = err?.response?.status;
      setForbidden(status === 403);
      setLoadError(
        status === 403
          ? null
          : err?.response?.data?.message || err.message || "Could not load co-ownership"
      );
      setParticipants([]);
      setInvitations([]);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    load();
  }, [load]);

  const isCreator = (row) =>
    row.is_creator ||
    (campaignOwnerId != null && String(row.user_id) === String(campaignOwnerId));

  const activeOwnerCount = participants.length;

  async function handleRemove(row) {
    // preConfirm + showValidationMessage keeps the modal open and displays the
    // server's own message. That matters here: the two refusals staff will
    // actually hit (CREATOR_PROTECTED, LAST_OWNER) are 409 bodies, and a
    // fire-and-forget confirm would swallow them.
    const result = await Swal.fire({
      title: `Remove ${row.display_name}?`,
      text:
        "They lose management access to this campaign immediately, and any " +
        "pending invitations they sent are revoked. This does not delete their account.",
      icon: "warning",
      showCancelButton: true,
      showCloseButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove",
      cancelButtonText: "Cancel",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        try {
          setBusyUserId(row.user_id);
          await collabApi.removeParticipant(campaignId, row.user_id);
        } catch (err) {
          const code = err?.response?.data?.code;
          const map = {
            CREATOR_PROTECTED:
              "This is the campaign creator. Their account is the anchor for the " +
              "issuer fee, the payout account and the signed agreement, so they " +
              "cannot be removed without transferring those first.",
            LAST_OWNER:
              "A campaign must keep at least one owner. Add another co-owner before " +
              "removing this one.",
          };
          Swal.showValidationMessage(
            map[code] ||
              err?.response?.data?.message ||
              err.message ||
              "Could not remove this co-owner"
          );
        } finally {
          setBusyUserId(null);
        }
      },
    });
    if (result.isConfirmed) {
      Swal.fire({ title: "Co-owner removed", icon: "success", timer: 2500, timerProgressBar: true });
      load();
    }
  }

  return (
    <div className="left">
      <h1 className="title">Co-ownership</h1>
      <div className="item">
        <div className="details profile-info" style={{ width: "100%" }}>
          {loading && <h5>Loading…</h5>}

          {loadError && (
            <div
              data-testid="coownership-error"
              style={{ color: "#d33", marginBottom: "12px" }}
            >
              Could not load co-ownership: {loadError}
            </div>
          )}

          {!loading && forbidden && (
            <p data-testid="coownership-forbidden" style={{ color: "#666" }}>
              Your role does not have access to this campaign&apos;s co-ownership details.
            </p>
          )}

          {!loading && !loadError && !forbidden && (
            <>
              <div style={{ marginBottom: "16px" }}>
                <strong>Co-owners ({activeOwnerCount})</strong>
                {participants.length === 0 ? (
                  <h5>No co-owners recorded.</h5>
                ) : (
                  <table className="tbl" style={{ width: "100%", marginTop: "8px" }}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Since</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody data-testid="coownership-participants">
                      {participants.map((row) => (
                        <tr key={row.user_id}>
                          <td>
                            {row.display_name}
                            {isCreator(row) && (
                              <span
                                style={{ marginLeft: "6px", fontSize: "12px", color: "#666" }}
                              >
                                (creator)
                              </span>
                            )}
                          </td>
                          <td>{row.email || "—"}</td>
                          <td>{row.participation_role || "OWNER"}</td>
                          <td>{row.joined_at ? dateFormat(row.joined_at) : "—"}</td>
                          <td style={{ textAlign: "right" }}>
                            {isCreator(row) ? (
                              // A25: not a disabled button — an explanation.
                              // A greyed-out control invites staff to keep
                              // clicking it and file a bug.
                              <span
                                style={{ fontSize: "12px", color: "#666" }}
                                title="The creator is the anchor for the issuer fee, the payout account and the signed agreement."
                              >
                                anchor account
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="draftapproveButton"
                                style={{ background: "#d33" }}
                                disabled={busyUserId === row.user_id}
                                onClick={() => handleRemove(row)}
                                data-testid={`coownership-remove-${row.user_id}`}
                              >
                                {busyUserId === row.user_id ? "…" : "Remove"}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div>
                <strong>Pending invitations ({invitations.length})</strong>
                {invitations.length === 0 ? (
                  <h5>No pending invitations.</h5>
                ) : (
                  <table className="tbl" style={{ width: "100%", marginTop: "8px" }}>
                    <thead>
                      <tr>
                        <th>Invited email</th>
                        <th>Invited by</th>
                        <th>Sent</th>
                        <th>Expires</th>
                      </tr>
                    </thead>
                    <tbody data-testid="coownership-invitations">
                      {invitations.map((inv) => (
                        <tr key={inv.id}>
                          <td>{inv.invited_email}</td>
                          <td>
                            {inv.InvitedBy
                              ? `${inv.InvitedBy.firstname || ""} ${inv.InvitedBy.lastname || ""}`.trim() ||
                                `User #${inv.InvitedBy.id}`
                              : "—"}
                          </td>
                          <td>{inv.createdAt ? dateFormat(inv.createdAt) : "—"}</td>
                          <td>{inv.expires_at ? dateFormat(inv.expires_at) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {/* Stated, not silently missing: revoke is flag-gated and would
                    503 on production, so it is not offered here. */}
                <p style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
                  Invitations are revoked by the campaign&apos;s own directors panel.
                  Only pending invitations are listed.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoOwnershipPanel;
